import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Logo from "../assets/logo.png";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { 
  FaHome, 
  FaUsers, 
  FaCrown,
  FaUserCircle,
  FaFileContract,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaTachometerAlt,
  FaShareAlt,
  FaWallet,
  FaExchangeAlt,
  FaChevronDown,
  FaInfoCircle,
  FaEnvelope,
  FaLayerGroup,
  FaSearch,
  FaShoppingCart
} from "react-icons/fa";

// ABM Group Colors
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

function Header() {
  const { user, logout } = useContext(AuthContext);
  const { getCartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      // setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu and dropdowns when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    setIsMoreDropdownOpen(false);
    setIsMobileSearchOpen(false);
  }, [location]);

  // Focus mobile search input when opened
  useEffect(() => {
    if (isMobileSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleMoreDropdown = () => setIsMoreDropdownOpen(!isMoreDropdownOpen);
  const toggleMobileSearch = () => setIsMobileSearchOpen(!isMobileSearchOpen);

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setIsMobileSearchOpen(false);
    } else {
      // If empty search, just go to products page
      navigate("/products");
      setIsMobileSearchOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const moreDropdown = document.getElementById('more-dropdown');
      const moreButton = document.getElementById('more-button');
      if (moreDropdown && moreButton && !moreDropdown.contains(event.target) && !moreButton.contains(event.target)) {
        setIsMoreDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigation items
  const mainNavItems = [
    { name: "Home", path: "/", icon: FaHome, public: true },
    { name: "Products", path: "/products", icon: FaTachometerAlt, public: true },
    { name: "Business Plan", path: "/business-plan", icon: FaFileContract, public: true },
  ];

  // Items for "More" dropdown
  const moreMenuItems = [
    { name: "Features", path: "/features", icon: FaCrown, public: true },
    { name: "About", path: "/about", icon: FaInfoCircle, public: true },
    { name: "Contact", path: "/contact", icon: FaEnvelope, public: true },
    { name: "Terms & Conditions", path: "/terms-and-conditions", icon: FaFileContract, public: true },
  ];

  const isActive = (path) => location.pathname === path;
  const isMoreItemActive = () => moreMenuItems.some(item => location.pathname === item.path);

  return (
    <header
      className={`fixed w-full top-0 z-[999] font-poppins transition-all duration-300 ${
        isScrolled 
          ? "bg-[#2563eb] shadow-lg py-2" 
          : "bg-[#2563eb] py-4"
      }`}
      style={{ 
        borderBottom: isScrolled ? 'none' : `2px solid rgba(255, 255, 255, 0.2)`
      }}
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex justify-between items-center">

          {/* Logo with ABM colors */}
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <div className="flex items-center">
              <img
                src={Logo}
                alt="ABM Group"
                className={`transition-all duration-300 ${
                  isScrolled ? "w-16 h-16" : "w-16 h-16"
                }`}
              />
            </div>
            {/* Company name with colored dots */}
            <div className="hidden sm:block">
              <div className="flex items-center gap-1">
                <span className="text-2xl font-bold text-white">ABM</span>
                <span className="text-lg font-medium text-white">GROUP</span>
                <span className="text-lg font-medium text-white">ONE</span>
              </div>
              <div className="flex gap-1 mt-0.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.pinkMagenta }}></span>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.goldenYellow }}></span>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.freshGreen }}></span>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.brightBlue }}></span>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.deepOrange }}></span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Main Navigation Items */}
            {mainNavItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center gap-1 text-white hover:text-white`}
                style={{
                  backgroundColor: isActive(item.path) ? colors.deepOrange : 'transparent',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = colors.lightOrange + '20';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <item.icon className="text-lg" />
                {item.name}
              </Link>
            ))}

            {/* More Dropdown */}
            <div className="relative">
              <button
                id="more-button"
                onClick={toggleMoreDropdown}
                className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center gap-1 text-white hover:text-white`}
                style={{
                  backgroundColor: isMoreItemActive() ? colors.deepOrange : 'transparent',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  if (!isMoreItemActive()) {
                    e.currentTarget.style.backgroundColor = colors.lightOrange + '20';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMoreItemActive()) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <FaLayerGroup className="text-lg" />
                More
                <FaChevronDown className={`text-xs transition-transform duration-200 ${isMoreDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* More Dropdown Menu */}
              {isMoreDropdownOpen && (
                <div 
                  id="more-dropdown"
                  className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 border border-slate-100 z-50"
                  style={{ animation: 'fadeIn 0.2s ease-out' }}
                >
                  {moreMenuItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsMoreDropdownOpen(false)}
                      className={`block px-4 py-2.5 text-sm transition-all duration-200 flex items-center gap-3 ${
                        isActive(item.path)
                          ? 'bg-slate-50'
                          : 'hover:bg-slate-50'
                      }`}
                      style={{
                        color: isActive(item.path) ? colors.deepOrange : colors.nearBlack
                      }}
                    >
                      <item.icon className={`text-base`} style={{ color: isActive(item.path) ? colors.deepOrange : colors.oliveBrown }} />
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Search Bar with Icon - Always Visible on Desktop */}
            {/* <form onSubmit={handleSearchSubmit} className="ml-2">
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-80 px-4 py-2 pl-10 pr-12 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    borderColor: colors.lightOrange + '40',
                    backgroundColor: 'white',
                    color: colors.nearBlack
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.deepOrange;
                    e.target.style.boxShadow = `0 0 0 2px ${colors.deepOrange}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.lightOrange + '40';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <button
                  type="submit"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-all duration-200"
                  style={{
                    color: colors.deepOrange,
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.lightOrange + '20';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <FaSearch size={14} />
                </button>
              </div>
            </form> */}

            {/* Cart Button */}
            {user && (
              <Link
                to="/buy-product"
                className="relative p-2 rounded-lg transition-all duration-200 ml-2"
                style={{ color: 'white' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <FaShoppingCart size={20} />
                {getCartCount() > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: colors.deepOrange,
                      color: 'white'
                    }}
                  >
                    {getCartCount()}
                  </span>
                )}
              </Link>
            )}

            {/* User Menu / Auth Buttons */}
            <div className="ml-4 pl-4 border-l border-white/30">
              {user ? (
                <div className="relative">
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200"
                    style={{ 
                      backgroundColor: isDropdownOpen ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                      color: 'white'
                    }}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r flex items-center justify-center text-white"
                      style={{ 
                        background: `linear-gradient(135deg, ${colors.brightBlue}, ${colors.navyBlue})`
                      }}
                    >
                      <FaUserCircle className="text-xl" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-white/80">{user.role || 'Member'}</p>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 border border-slate-100 z-50">
                      <Link
                        to="/dashboard"
                        className="block px-4 py-2 text-sm hover:bg-slate-50 transition flex items-center gap-2"
                        style={{ color: colors.nearBlack }}
                      >
                        <FaTachometerAlt style={{ color: colors.freshGreen }} /> Dashboard
                      </Link>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm hover:bg-slate-50 transition flex items-center gap-2"
                        style={{ color: colors.nearBlack }}
                      >
                        <FaUserCircle style={{ color: colors.brightBlue }} /> Profile
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setIsDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition flex items-center gap-2"
                        style={{ color: colors.reddishBrown }}
                      >
                        <FaSignOutAlt style={{ color: colors.deepOrange }} /> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="px-5 py-2 rounded-lg text-base font-medium transition-all duration-200 text-white hover:text-white"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="px-5 py-2 rounded-lg text-base font-medium text-white transition-all duration-200 hover:shadow-lg"
                    style={{ 
                      background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})`,
                    }}
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Right Icons (Search + Menu) */}
          <div className="flex items-center gap-2 lg:hidden">
            {/* Mobile Search Button */}
            {/* <button
              onClick={toggleMobileSearch}
              className="p-3 rounded-lg transition-all duration-200"
              style={{ 
                backgroundColor: isMobileSearchOpen ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: 'white'
              }}
            >
              <FaSearch size={20} style={{ color: 'white' }} />
            </button> */}

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="p-3 rounded-lg transition-all duration-200"
              style={{ 
                backgroundColor: isMenuOpen ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: 'white'
              }}
            >
              {isMenuOpen ? <FaTimes size={24} style={{ color: 'white' }} /> : <FaBars size={24} style={{ color: 'white' }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar (expanded when search icon is clicked) */}
      {isMobileSearchOpen && (
        <div className="lg:hidden px-4 py-3 border-t border-white/20">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full px-4 py-3 pl-10 pr-12 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: colors.lightOrange + '40',
                  backgroundColor: 'white',
                  color: colors.nearBlack
                }}
                autoFocus
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-all"
                style={{
                  color: colors.deepOrange,
                  backgroundColor: 'transparent'
                }}
              >
                <FaSearch size={14} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mobile Menu - Slide from right */}
      {isMenuOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] lg:hidden">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={closeMenu}
            ></div>
            
            {/* Menu Panel */}
            <div 
              className="absolute right-0 top-0 h-full w-80 bg-white shadow-2xl overflow-y-auto"
              style={{ 
                animation: 'slideIn 0.3s ease-out'
              }}
            >
              <div className="p-6">
                {/* Mobile Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-2">
                    <img src={Logo} alt="Logo" className="w-12 h-12" />
                    <div>
                      <span className="text-xl font-bold text-white">ABM</span>
                      <span className="text-sm block text-white/80">GROUP ONE</span>
                    </div>
                  </div>
                  <button 
                    onClick={closeMenu}
                    className="p-2 rounded-lg hover:bg-slate-100 transition"
                  >
                    <FaTimes size={20} style={{ color: colors.deepOrange }} />
                  </button>
                </div>

                {/* User Info if logged in */}
                {user && (
                  <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: colors.brightBlue + '10' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r flex items-center justify-center text-white"
                        style={{ 
                          background: `linear-gradient(135deg, ${colors.brightBlue}, ${colors.navyBlue})`
                        }}
                      >
                        <FaUserCircle size={24} />
                      </div>
                      <div className="cursor-pointer" onClick={() => {
                        navigate("/dashboard");
                        closeMenu();
                      }}>
                        <p className="font-bold">{user.name}</p>
                        <p className="text-sm" style={{ color: colors.oliveBrown }}>{user.email}</p>
                        <p className="text-sm" style={{ color: colors.oliveBrown }}>{user.mobile}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Links */}
                <ul className="space-y-2">
                  {/* Main Links */}
                  {mainNavItems.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.path}
                        onClick={closeMenu}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all ${
                          isActive(item.path)
                            ? 'text-white'
                            : 'hover:bg-slate-50'
                        }`}
                        style={{
                          backgroundColor: isActive(item.path) ? colors.deepOrange : 'transparent',
                          color: isActive(item.path) ? 'white' : colors.nearBlack
                        }}
                      >
                        <item.icon className="text-xl" />
                        {item.name}
                      </Link>
                    </li>
                  ))}

                  {/* More Dropdown Section in Mobile */}
                  <div className="mt-2">
                    <p className="px-4 text-xs font-semibold uppercase mb-2" style={{ color: colors.oliveBrown }}>
                      More
                    </p>
                    {moreMenuItems.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.path}
                          onClick={closeMenu}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all ${
                            isActive(item.path)
                              ? 'text-white'
                              : 'hover:bg-slate-50'
                          }`}
                          style={{
                            backgroundColor: isActive(item.path) ? colors.deepOrange : 'transparent',
                            color: isActive(item.path) ? 'white' : colors.nearBlack
                          }}
                        >
                          <item.icon className="text-xl" />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </div>

                  {/* User Links if logged in */}
                  {/* {user && (
                    <>
                      <div className="my-4 border-t border-slate-100"></div>
                      <p className="px-4 text-xs font-semibold uppercase mb-2" style={{ color: colors.oliveBrown }}>
                        Member Area
                      </p>
                      <li key="dashboard">
                        <Link
                          to="/dashboard"
                          onClick={closeMenu}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all"
                          style={{
                            backgroundColor: location.pathname === "/dashboard" ? colors.freshGreen : 'transparent',
                            color: location.pathname === "/dashboard" ? 'white' : colors.nearBlack
                          }}
                        >
                          <FaTachometerAlt className="text-xl" />
                          Dashboard
                        </Link>
                      </li>
                      <li key="wallet">
                        <Link
                          to="/wallet"
                          onClick={closeMenu}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all"
                          style={{
                            backgroundColor: location.pathname === "/wallet" ? colors.freshGreen : 'transparent',
                            color: location.pathname === "/wallet" ? 'white' : colors.nearBlack
                          }}
                        >
                          <FaWallet className="text-xl" />
                          Wallet
                        </Link>
                      </li>
                      <li key="team">
                        <Link
                          to="/team"
                          onClick={closeMenu}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all"
                          style={{
                            backgroundColor: location.pathname === "/team" ? colors.freshGreen : 'transparent',
                            color: location.pathname === "/team" ? 'white' : colors.nearBlack
                          }}
                        >
                          <FaUsers className="text-xl" />
                          Team
                        </Link>
                      </li>
                      <li key="buy-share">
                        <Link
                          to="/buy-share"
                          onClick={closeMenu}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all"
                          style={{
                            backgroundColor: location.pathname === "/buy-share" ? colors.freshGreen : 'transparent',
                            color: location.pathname === "/buy-share" ? 'white' : colors.nearBlack
                          }}
                        >
                          <FaShareAlt className="text-xl" />
                          Buy Share
                        </Link>
                      </li>
                      <li key="transfer">
                        <Link
                          to="/transfer"
                          onClick={closeMenu}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all"
                          style={{
                            backgroundColor: location.pathname === "/transfer" ? colors.freshGreen : 'transparent',
                            color: location.pathname === "/transfer" ? 'white' : colors.nearBlack
                          }}
                        >
                          <FaExchangeAlt className="text-xl" />
                          Transfer
                        </Link>
                      </li>
                      <li key="level-income">
                        <Link
                          to="/level-income"
                          onClick={closeMenu}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all"
                          style={{
                            backgroundColor: location.pathname === "/level-income" ? colors.freshGreen : 'transparent',
                            color: location.pathname === "/level-income" ? 'white' : colors.nearBlack
                          }}
                        >
                          <FaShareAlt className="text-xl" />
                          Level Income
                        </Link>
                      </li>
                    </>
                  )} */}
                </ul>

                {/* Auth Buttons for Mobile */}
                {!user && (
                  <div className="mt-8 space-y-3">
                    <Link
                      to="/login"
                      onClick={closeMenu}
                      className="block w-full text-center px-4 py-3 rounded-lg text-base font-medium border-2 transition"
                      style={{ 
                        borderColor: colors.brightBlue,
                        color: colors.navyBlue
                      }}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={closeMenu}
                      className="block w-full text-center px-4 py-3 rounded-lg text-base font-medium text-white transition"
                      style={{ 
                        background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})`,
                      }}
                    >
                      Register Now
                    </Link>
                  </div>
                )}

                {/* Logout Button for Mobile */}
                {user && (
                  <button
                    onClick={() => {
                      logout();
                      closeMenu();
                    }}
                    className="mt-8 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-base font-medium transition"
                    style={{ 
                      backgroundColor: colors.reddishBrown + '10',
                      color: colors.reddishBrown
                    }}
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                )}

                {/* Contact Info */}
                <div className="mt-8 p-4 rounded-lg bg-slate-50">
                  <p className="text-xs text-center" style={{ color: colors.oliveBrown }}>
                    Need Help? <br />
                    <span className="font-bold" style={{ color: colors.deepOrange }}>8887355992</span>
                  </p>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </header>
  );
}

export default Header;